import React, { ReactNode, createContext, useContext, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Programme } from 'src/dao/core-dao'
import { cn } from 'src/lib/utils'

interface SeriesCardContextValue {
  series: Programme
  onAction?: () => void
}

const SeriesCardContext = createContext<SeriesCardContextValue | undefined>(undefined)

export const useSeriesCardContext = (): SeriesCardContextValue => {
  const context = useContext(SeriesCardContext)
  if (!context) {
    throw new Error('SeriesCard components must be used within SeriesCard')
  }
  return context
}

interface SeriesCardProps {
  series: Programme
  className?: string
  children: ReactNode
  onAction?: () => void
  /**
   * When the card is a link (no `onAction`), navigates here. Defaults to `/programmes/{series.id}`.
   * Use for deep links (e.g. week + next workout) from active / keep-going cards.
   */
  to?: string
  /** When provided with `onAction`, the outer surface is keyboard-focusable and exposes this label (e.g. homepage cards that are not links). */
  actionAriaLabel?: string
}

export const SeriesCard = ({
  series,
  className,
  children,
  onAction,
  to,
  actionAriaLabel,
}: SeriesCardProps): React.ReactElement => {
  const contextValue = useMemo(
    () => ({
      series,
      onAction,
    }),
    [series, onAction],
  )

  const cardContent = (
    <SeriesCardContext.Provider value={contextValue}>
      <div className={cn('relative overflow-hidden cursor-pointer aspect-[6/4] rounded-lg', className)}>{children}</div>
    </SeriesCardContext.Provider>
  )

  if (onAction) {
    const runAction = (): void => {
      onAction()
    }

    return (
      <div
        className="h-full cursor-pointer"
        role={actionAriaLabel ? 'button' : undefined}
        tabIndex={actionAriaLabel ? 0 : undefined}
        aria-label={actionAriaLabel}
        onClick={(e) => {
          if ((e.target as HTMLElement).closest('button')) {
            return
          }
          runAction()
        }}
        onKeyDown={(e) => {
          if (!actionAriaLabel) {
            return
          }
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            if ((e.target as HTMLElement).closest('button')) {
              return
            }
            runAction()
          }
        }}
      >
        {cardContent}
      </div>
    )
  }

  const linkTo = to ?? `/programmes/${series.id}`

  return (
    <Link
      to={linkTo}
      className="block no-underline h-full"
      onClick={(e) => {
        // Cards nest interactive controls (e.g. dismiss). Invalid `<a><button>` can still navigate in some
        // browsers; prevent following the link when the click came from a button.
        if ((e.target as HTMLElement).closest('button')) {
          e.preventDefault()
        }
      }}
    >
      {cardContent}
    </Link>
  )
}
